import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import PassportIdentityBadge from './PassportIdentityBadge';
import '../../App.css';

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const StatusBadge = ({ status }) => {
  const colors = {
    VALID: '#10B981',
    EXPIRING: '#F59E0B',
    EXPIRED: '#EF4444',
    MISSING: '#6B7280',
    READY: '#10B981',
    AT_RISK: '#F59E0B',
    NOT_READY: '#EF4444',
  };
  const color = colors[status] || '#6B7280';
  return (
    <span className="public-passport-status-badge" style={{ backgroundColor: color }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

export default function PublicPassportView() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!shareToken) return;
    axios
      .get(`${API_URL}/api/passport/${shareToken}`)
      .then((res) => setData(res.data))
      .catch((err) => {
        setError(err.response?.status === 404 ? 'This link is invalid or has expired.' : 'Unable to load compliance passport.');
      })
      .finally(() => setLoading(false));
  }, [shareToken]);

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="public-passport-page">
        <div className="public-passport-loading">
          <div className="loading-spinner" />
          <p>Loading compliance passport…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="public-passport-page">
        <div className="public-passport-error">
          <h1>Compliance Passport</h1>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-passport-page">
      <div className="public-passport-actions no-print">
        <button type="button" className="public-passport-print-btn" onClick={handlePrint} aria-label="Print">
          Print
        </button>
      </div>

      <div className="public-passport-document">
        <header className="public-passport-header">
          <div className="public-passport-brand">SOVRYNN</div>
          <div className="public-passport-subtitle">Compliance Passport</div>
          <PassportIdentityBadge identityVerified={data.identityVerified} />
          <div className="public-passport-meta">
            <span>Ref: {data.reference}</span>
            <span>As of: {formatDate(data.generatedAt)}</span>
            {data.expiresAt && <span>Link expires: {formatDate(data.expiresAt)}</span>}
          </div>
        </header>

        {data.driver && (
          <section className="public-passport-section">
            <h2>Driver</h2>
            <div className="public-passport-grid">
              <div><span className="label">Name</span> {data.driver.name}</div>
              {data.driver.cdlState && <div><span className="label">CDL State</span> {data.driver.cdlState}</div>}
              {data.driver.dotNumber && <div><span className="label">DOT</span> {data.driver.dotNumber}</div>}
            </div>
          </section>
        )}

        {data.doc_completeness && (
          <section className="public-passport-section">
            <h2>Document completeness</h2>
            <div className="public-passport-score-row">
              <span className="score-value">{data.doc_completeness.driScore ?? '—'}/100</span>
              <StatusBadge status={data.doc_completeness.status} />
            </div>
            {data.doc_completeness.documents?.length > 0 && (
              <ul className="public-passport-doc-list">
                {data.doc_completeness.documents.map((d, i) => (
                  <li key={i}>
                    <span>{d.name}</span>
                    <StatusBadge status={d.status} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {data.medical_card && (
          <section className="public-passport-section">
            <h2>Medical card</h2>
            <div className="public-passport-score-row">
              <StatusBadge status={data.medical_card.status} />
              {data.medical_card.expirationDate && (
                <span className="label">Expires {formatDate(data.medical_card.expirationDate)}</span>
              )}
            </div>
          </section>
        )}

        {data.authority && (
          <section className="public-passport-section">
            <h2>Authority & insurance</h2>
            <div className="public-passport-grid">
              <div><span className="label">Legal name</span> {data.authority.legalName}</div>
              <div><span className="label">DOT</span> {data.authority.dotNumber}</div>
              <div><span className="label">Status</span> <StatusBadge status={data.authority.operatingStatus} /></div>
              <div><span className="label">Insurance on file</span> {data.authority.insuranceOnFile ? 'Yes' : 'No'}</div>
              {data.authority.healthScore != null && (
                <div><span className="label">Health score</span> {data.authority.healthScore}/100</div>
              )}
            </div>
          </section>
        )}

        {data.insurance_status && (
          <section className="public-passport-section">
            <h2>Insurance status</h2>
            <div className="public-passport-score-row">
              <StatusBadge status={data.insurance_status.status} />
              {data.insurance_status.expirationDate && (
                <span>Expires {formatDate(data.insurance_status.expirationDate)}</span>
              )}
            </div>
          </section>
        )}

        {data.vehicles_readiness?.vehicles?.length > 0 && (
          <section className="public-passport-section">
            <h2>Vehicles readiness</h2>
            <p className="public-passport-count">{data.vehicles_readiness.count} vehicle(s)</p>
            <ul className="public-passport-vehicle-list">
              {data.vehicles_readiness.vehicles.map((v, i) => (
                <li key={i}>
                  {v.year} {v.make} {v.model}
                  {v.readinessScore != null && (
                    <span className="score-badge">{v.readinessScore}/100</span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {data.inspection_history_summary && (
          <section className="public-passport-section">
            <h2>Inspection summary</h2>
            <p className="public-passport-count">{data.inspection_history_summary.vehicleCount} vehicle(s)</p>
            {data.inspection_history_summary.upcomingExpirations?.length > 0 && (
              <ul className="public-passport-exp-list">
                {data.inspection_history_summary.upcomingExpirations.map((e, i) => (
                  <li key={i}>{e.item} — {formatDate(e.expiresAt)} ({e.daysUntil} days)</li>
                ))}
              </ul>
            )}
          </section>
        )}

        {data.audit_readiness && (
          <section className="public-passport-section">
            <h2>Audit readiness</h2>
            <div className="public-passport-score-row">
              <span className="score-value">{data.audit_readiness.readinessScore ?? '—'}/100</span>
              <StatusBadge status={data.audit_readiness.status} />
            </div>
            {(data.audit_readiness.missingCount > 0 || data.audit_readiness.dueSoonCount > 0) && (
              <p className="public-passport-count">
                {data.audit_readiness.missingCount} missing, {data.audit_readiness.dueSoonCount} due soon
              </p>
            )}
          </section>
        )}

        <footer className="public-passport-footer">
          <p>Status and verified indicators only. No sensitive documents. Verify at sovrynn.com</p>
        </footer>
      </div>
    </div>
  );
}
